import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react';

type RunningChatsStore = {
  watch: (chatName: string) => void;
  unwatch: (chatName: string) => void;
  subscribe: (callback: () => void) => () => void;
  getSnapshot: () => ReadonlySet<string>;
};

const RunningChatsContext = createContext<RunningChatsStore | null>(null);

export const RunningChatsProvider = ({ children }: { children: ReactNode }) => {
  const chatsRef = useRef(new Set<string>());
  const snapshotRef = useRef<ReadonlySet<string>>(chatsRef.current);
  const listenersRef = useRef(new Set<() => void>());

  const emit = useCallback(() => {
    snapshotRef.current = new Set(chatsRef.current);
    for (const listener of listenersRef.current) {
      listener();
    }
  }, []);

  const watch = useCallback(
    (chatName: string) => {
      if (!chatsRef.current.has(chatName)) {
        chatsRef.current.add(chatName);
        emit();
      }
    },
    [emit],
  );

  const unwatch = useCallback(
    (chatName: string) => {
      if (chatsRef.current.has(chatName)) {
        chatsRef.current.delete(chatName);
        emit();
      }
    },
    [emit],
  );

  const subscribe = useCallback((callback: () => void) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  return (
    <RunningChatsContext.Provider
      value={{ watch, unwatch, subscribe, getSnapshot }}
    >
      {children}
    </RunningChatsContext.Provider>
  );
};

export const useWatchRunningChat = () => {
  const store = useContext(RunningChatsContext);
  const noop = useCallback(() => {}, []);
  return {
    watch: store?.watch ?? noop,
    unwatch: store?.unwatch ?? noop,
  };
};

const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY_SUBSCRIBE = () => () => {};
const EMPTY_SNAPSHOT = () => EMPTY_SET;

export const useRunningChatNames = (): ReadonlySet<string> => {
  const store = useContext(RunningChatsContext);
  return useSyncExternalStore(
    store?.subscribe ?? EMPTY_SUBSCRIBE,
    store?.getSnapshot ?? EMPTY_SNAPSHOT,
  );
};
